/**
 * Template Image Processing Utility
 *
 * Handles compression and optimization of template preview screenshots
 * before uploading to Supabase Storage.
 *
 * Uses browser-image-compression for efficient client-side processing.
 */

import imageCompression from 'browser-image-compression';

/**
 * Standard preview dimensions for template thumbnails
 * These are target dimensions, actual may vary based on aspect ratio
 */
export const PREVIEW_DIMENSIONS = {
  width: 600,
  height: 800  // Increased for better preview quality
};

/**
 * Process template preview screenshot for upload
 *
 * Compresses and optimizes the image to reduce file size while
 * maintaining high quality for preview thumbnails.
 *
 * Processing steps:
 * 1. Compress to target file size (max 1MB for high-res previews)
 * 2. Maintain high resolution (max 1200px width)
 * 3. Convert to WebP format for better compression
 * 4. Use web worker to avoid blocking main thread
 *
 * @param blob - Screenshot blob from capturePreviewDialog
 * @returns Promise<Blob> - Processed and compressed image
 * @throws Error if processing fails
 */
export const processTemplatePreview = async (
  blob: Blob
): Promise<Blob> => {
  try {
    const originalSize = blob.size;
    console.log('[processTemplatePreview] Original size:', (originalSize / 1024).toFixed(2), 'KB');

    // Compression options optimized for high-quality template previews
    const options = {
      maxSizeMB: 1,  // Allow up to 1MB for high-quality previews
      maxWidthOrHeight: 1200,  // Higher resolution for crisp display
      useWebWorker: true,  // Offload to web worker (don't block UI)
      fileType: 'image/webp' as const,  // WebP for best compression
      initialQuality: 0.9  // Higher quality for better preview (90%)
    };

    // Compress the image
    const compressed = await imageCompression(blob, options);

    const compressedSize = compressed.size;
    const reduction = ((1 - compressedSize / originalSize) * 100).toFixed(1);

    console.log('[processTemplatePreview] Compressed size:', (compressedSize / 1024).toFixed(2), 'KB');
    console.log('[processTemplatePreview] Size reduction:', reduction + '%');

    // Warn if final size is still large (but acceptable for high-quality previews)
    if (compressedSize > 1024 * 1024) {
      console.warn('[processTemplatePreview] Compressed file exceeds 1MB target:',
        (compressedSize / 1024).toFixed(2), 'KB'
      );
    }

    return compressed;

  } catch (error) {
    console.error('[processTemplatePreview] Processing failed:', error);
    throw new Error(
      `Image processing failed: ${error instanceof Error ? error.message : 'Unknown error'}`
    );
  }
};

/**
 * Validate image blob before processing
 *
 * Checks for valid blob, reasonable size, and correct type
 *
 * @param blob - Blob to validate
 * @throws Error if validation fails
 */
export const validateImageBlob = (blob: Blob): void => {
  if (!blob) {
    throw new Error('No image blob provided');
  }

  if (blob.size === 0) {
    throw new Error('Image blob is empty');
  }

  // Max 10MB uncompressed (should be much smaller after screenshot)
  if (blob.size > 10 * 1024 * 1024) {
    throw new Error(
      `Image too large: ${(blob.size / 1024 / 1024).toFixed(2)}MB (max 10MB)`
    );
  }

  // Check if it's an image type
  if (!blob.type.startsWith('image/')) {
    throw new Error(`Invalid blob type: ${blob.type} (expected image/*)`);
  }

  console.log('[validateImageBlob] Validation passed:', {
    type: blob.type,
    size: (blob.size / 1024).toFixed(2) + ' KB'
  });
};

/**
 * Process and validate in one step
 *
 * Convenience function that validates then processes
 *
 * @param blob - Screenshot blob
 * @returns Promise<Blob> - Processed image
 */
export const processTemplatePreviewWithValidation = async (
  blob: Blob
): Promise<Blob> => {
  validateImageBlob(blob);
  return processTemplatePreview(blob);
};
